/**
 * köbi · Mischbox-Card Background-Brightness Auto-Kontrast
 * --------------------------------------------------------
 * Wenn eine Mischbox-Card ein `custom.background_image`-Metafeld gesetzt
 * hat, wird das Hintergrundbild zur tatsaechlichen Darstellung. Liquid kann
 * Bilder nicht pixel-analysieren — die Initial-Textfarbe kommt also aus dem
 * Auto-Kontrast der `custom.kartenfarbe` (Fallback). Dieses Skript laeuft
 * danach client-seitig, misst die Bildhelligkeit via Canvas (W3C-Luminanz,
 * 16×16-Sample, identisches Pattern wie `koebi-gallery-brightness.js`) und
 * ueberschreibt die `color`-CSS-Eigenschaft der Card, wenn sie mit der
 * Bildhelligkeit nicht zur Lesbarkeit passt.
 */
(function () {
  'use strict';

  var TAG = '[koebi-card-bg]';
  /* Schwellwert hell/dunkel auf Bildern. Liquid nutzt fuer reine Farbflaechen
     (custom.kartenfarbe) 128, hier liegen wir bewusst hoeher, weil Fotos mit
     mittlerer Luma (z.B. lila/violett bei ~130-140) fuer das Auge dunkler
     wirken als der einfache Rec.601-Luma vorgibt (Blau-Gewicht 0.114
     unterbewertet die wahrgenommene Dunkelheit). 150 hat sich an realen
     Mischbox-Bildern als guter Trenner erwiesen — siehe SESSION-LOG. */
  var THRESHOLD = 150;
  var DARK_TEXT = '#1f1f1f';            /* gleiche Hex wie Liquid-Side */
  var LIGHT_TEXT = '#f9f8f6';
  var BORDER_DEFAULT = '#D6D6D6';       /* Token border/default — nur am hellen Tag */

  function luminaFromData(data) {
    var total = 0;
    var count = data.length / 4;
    for (var i = 0; i < data.length; i += 4) {
      total += data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
    }
    return total / count;
  }

  /**
   * Misst die wahrgenommene Helligkeit (Luma) eines bereits decoded
   * DOM-Image-Elements. Voraussetzung: das <img> wurde mit
   * crossorigin="anonymous" geladen, sonst wird das Canvas "tainted"
   * und getImageData() wirft SecurityError.
   */
  function measureFromImg(imgEl) {
    try {
      var canvas = document.createElement('canvas');
      canvas.width = 16;
      canvas.height = 16;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(imgEl, 0, 0, 16, 16);
      var data = ctx.getImageData(0, 0, 16, 16).data;
      return luminaFromData(data);
    } catch (e) {
      console.warn(TAG, 'measureFromImg → tainted canvas:', e.message, '(src:', imgEl.currentSrc || imgEl.src, ')');
      return null;
    }
  }

  /**
   * Fallback: holt das Bild via fetch (CORS-Mode) und sampelt via
   * createImageBitmap. Funktioniert auch wenn der ursprueengliche <img>
   * Tag aus irgendeinem Grund tainted ist.
   */
  function measureFromFetch(src) {
    if (typeof createImageBitmap !== 'function') return Promise.resolve(null);
    return fetch(src, { mode: 'cors' })
      .then(function (res) {
        if (!res.ok) throw new Error('http ' + res.status);
        return res.blob();
      })
      .then(function (blob) { return createImageBitmap(blob, { resizeWidth: 16, resizeHeight: 16, resizeQuality: 'low' }); })
      .then(function (bitmap) {
        var canvas = document.createElement('canvas');
        canvas.width = 16;
        canvas.height = 16;
        var ctx = canvas.getContext('2d');
        ctx.drawImage(bitmap, 0, 0, 16, 16);
        bitmap.close && bitmap.close();
        var data = ctx.getImageData(0, 0, 16, 16).data;
        return luminaFromData(data);
      })
      .catch(function (e) {
        console.warn(TAG, 'measureFromFetch failed for', src, '→', e.message);
        return null;
      });
  }

  function applyBrightness(card, brightness) {
    var isLight = brightness > THRESHOLD;
    /* Card-Text: bei hellem BG dunkel, bei dunklem BG hell. */
    var textColor = isLight ? DARK_TEXT : LIGHT_TEXT;
    /* Badge: gleiche Logik invertiert — bei hellem BG heller Tag mit dunklem
       Text, bei dunklem BG dunkler Tag mit hellem Text. Tag-Text = Card-Text,
       Tag-BG ist die jeweils andere Farbe. */
    var badgeBg = isLight ? LIGHT_TEXT : DARK_TEXT;
    var badgeBorder = isLight ? BORDER_DEFAULT : 'transparent';
    card.style.setProperty('color', textColor);
    card.style.setProperty('--koebi-badge-bg', badgeBg);
    card.style.setProperty('--koebi-badge-text', textColor);
    card.style.setProperty('--koebi-badge-border', badgeBorder);
    /* On-Dark-Flag: triggert die Font-Weight-Reduktion (-50) in base.css.
       Weiße Schrift wirkt auf dunklem Grund optisch fetter; mit Bricolage
       als Variable Font können wir das per Token-Override kompensieren. */
    if (isLight) {
      card.removeAttribute('data-koebi-on-dark');
    } else {
      card.setAttribute('data-koebi-on-dark', 'true');
    }
  }

  function applyCardContrast(card) {
    var bg = card.querySelector('.koebi-mischbox-card__bg');
    if (!bg) return;                    /* kein Bild → Liquid-Farbe bleibt */

    function compute() {
      /* erst direkt vom DOM-Image probieren */
      var b = measureFromImg(bg);
      if (b != null) {
        applyBrightness(card, b);
        return;
      }

      /* Fallback: separater CORS-Fetch falls Canvas tainted ist */
      var src = bg.currentSrc || bg.src;
      measureFromFetch(src).then(function (bf) {
        if (bf == null) return;
        applyBrightness(card, bf);
      });
    }

    function waitAndCompute() {
      if (typeof bg.decode === 'function') {
        bg.decode().then(compute).catch(compute);
      } else {
        compute();
      }
    }

    if (bg.complete && bg.naturalWidth) {
      waitAndCompute();
    } else {
      bg.addEventListener('load', waitAndCompute, { once: true });
    }
  }

  function init() {
    document.querySelectorAll('.koebi-mischbox-card').forEach(applyCardContrast);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
